import { API_BASE_URL } from "./advancedSettings";

/**
 * Asks the API to delete everything saved under the signed-in account that is keyed by its uid: saved transcripts in every
 * room, the audience questions and networking contacts of its streams, and its developer API keys. Authenticated with the
 * login token (not a bare uid). Throws if the API does not confirm, so the caller never deletes the login first.
 */
export async function deleteAccountData(getToken: () => Promise<string>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/account/DeleteMyData`, {
    method: "POST",
    headers: { Authorization: `Bearer ${await getToken()}` },
  });
  if (!response.ok) throw new Error(`Deleting your saved data failed (${response.status})`);
}

export interface AccountDeletionSteps {
  /** Every stream the account has (its profile's stream and the API's list), or none. Must resolve BEFORE the profile is removed. */
  readStreamNames: () => Promise<string[]>;
  /** account/DeleteMyData: transcripts, questions, contacts, developer keys. */
  deleteData: () => Promise<void>;
  /** stream/DeleteStream: the stream itself (folder, row, design). */
  deleteStream: (name: string) => Promise<void>;
  removeProfile: () => Promise<void>;
  deleteLogin: () => Promise<void>;
}

/**
 * The order matters. The stream names have to be read before the profile is removed, and the saved data has to be deleted
 * while the login still exists (it authenticates the call). If the data cannot be deleted we stop, leaving the account
 * intact so the person can try again, instead of deleting the login and leaving their transcripts behind with no way to remove them.
 * A failure to delete a stream itself (for example the name is registered to someone else) does not stop the deletion, nor the
 * other streams: the personal data is already gone, and the person must not be trapped in an account they cannot delete.
 */
export async function deleteAccount(steps: AccountDeletionSteps): Promise<void> {
  const names = Array.from(new Set((await steps.readStreamNames()).filter(Boolean).map((n) => n.toLowerCase())));
  await steps.deleteData();
  for (const name of names) {
    try { await steps.deleteStream(name); }
    catch (error) { console.error(`Could not delete the stream ${name}; continuing with the account deletion.`, error); }
  }
  await steps.removeProfile();
  await steps.deleteLogin();
}
